// src/App.tsx
import { Route, Routes } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { usePageTracking } from './hooks/usePageTracking';
import MainContainer from './components/Main-Container';
import CompleteProfile from './components/Complete-Profile';
import CompleteProfileAvatar from './components/Complete-Profile-Avatar';
import CreatePrompt from './components/Create-Prompt';
import 'bootstrap/dist/css/bootstrap.min.css';
import './components/modal-fixes.css';
import CreatePortfolio from './components/Create-Portfolio';
import PageNotFound from './components/PageNotFound';
import UserProfile from './components/UserProfile';
import Dashboard from './components/Dashboard';
import NotificationsPage from './components/Notifications/NotificationsPage';
import WebinarRegistration from './components/Dashboard/WebinarRegistration';
import CertificateProgram from './components/Dashboard/CertificateProgram';
import TrendsSource from './components/Dashboard/TrendsSource';
import TrendsListPage from './components/Dashboard/TrendsListPage';
import RecentActivityPage from './components/Dashboard/RecentActivityPage';
import RateMeComingSoon from './components/Dashboard/RateMeComingSoon';
import NegotiatorCenter from './components/Dashboard/NegotiatorCenter';
import PrivateRoute from './components/HOCs/PrivateRoute';
import Login from './components/Login';
import LoginSuccess from './components/LoginSuccess';
import SignupComplete from './components/SignupComplete';
import GoogleOAuthCallback from './components/GoogleOAuthCallback';
import Settings from './components/Settings';
import SecurityPage from './components/Settings/SecurityPage';
import PrivacyPage from './components/Settings/PrivacyPage';
import AccountConnections from './components/AccountConnections';
import { PublicRoute } from './components/HOCs/PublicRoute';
import ResetPassword from './components/ResetPassword';
import VerifyEmail from './components/VerifyEmail';
import Analytics from './components/Analytics';
import CandidateProfile from './components/CandidateProfile';
import PublicUserProfile from './components/PublicUserProfile';
import PublicCalendarPage from './components/PublicPreview/PublicCalendarPage';
import WelcomePage from './components/WelcomePage';
import FastOnboarding from './components/FastOnboarding';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ClientUpdateBanner from './components/ClientUpdateBanner';
import ChatDemo from './components/ChatDemo';
import MultiStepFlow from './components/MultiStepFlow';
import ChatWindow from './components/ChatWindow';
import ChatApp from './components/ChatApp';
import ChatSidebar from './components/ChatSidebar';
import MessagesHub from './components/MessageHub';
import TailwindLayout from './layouts/TailwindLayout';
import QuestionsHub from './components/MessageHub/QuestionsHub';
import ContactsFlow from './components/MessageHub/ContactsFlow';
import PublicCalendarView from './components/LiveCalendar/PublicCalendarView';
import MessageRequestList from './components/MessageHub/MessageRequestList';
import PromptsPage from './components/Prompts';
import ReferenceVerification from './components/ReferenceVerification';
import ReferenceDetailPage from './components/Dashboard/ReferenceDetailPage';
import ReferenceFeedbackListPage from './components/Dashboard/ReferenceFeedbackListPage';
import TermsOfServicePage from './components/Legal/TermsOfServicePage';
import PrivacyPolicyPage from './components/Legal/PrivacyPolicyPage';
import CookiePolicyPage from './components/Legal/CookiePolicyPage';
import CommunityStandardsPage from './components/Legal/CommunityStandardsPage';
import CandidateContentPolicyPage from './components/Legal/CandidateContentPolicyPage';

export type UserType = {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  gender?: string;
  dateOfBirth?: string;
  country?: string;
  state?: string;
  city?: string;
  profileImg?: string;
  resume?: string;
  resumeFileName?: string;
  phoneNumber?: string;
  username?: string;
  company?: string;
  willingToRelocate?: string;
  workArrangements?: string;
  timeToStart?: string;
  linkedin?: string;
  otherSocial?: string;
};

function App() {
  usePageTracking();
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  if (!googleClientId) {
    console.warn('Google Client ID is not configured in environment variables');
  }

  return (
    <GoogleOAuthProvider clientId={googleClientId || ''}>
      <>
        {/* <Router> */}
        <Routes>
          <Route
            path="/"
            element={
              <PublicRoute restricted>
                <Login />
              </PublicRoute>
            }
          />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/reference/verify" element={<ReferenceVerification />} />
          <Route path="/references/verify" element={<ReferenceVerification />} />
          <Route path="/login/success" element={<LoginSuccess />} />
          <Route path="/signup/complete" element={<SignupComplete />} />
          <Route path="/terms-of-service" element={<TermsOfServicePage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/cookie-policy" element={<CookiePolicyPage />} />
          <Route path="/community-standards" element={<CommunityStandardsPage />} />
          <Route path="/candidate-content-policy" element={<CandidateContentPolicyPage />} />
          <Route path="/auth/google/callback" element={<GoogleOAuthCallback />} />
          <Route path="/" element={<MainContainer />}>
            <Route
              path="/complete-profile"
              element={
                <PrivateRoute>
                  <CompleteProfile />
                </PrivateRoute>
              }
            />
            <Route
              path="/onboarding"
              element={
                <PrivateRoute>
                  <FastOnboarding />
                </PrivateRoute>
              }
            />
            <Route
              path="/create-portfolio"
              element={
                <PrivateRoute>
                  <CreatePortfolio />
                </PrivateRoute>
              }
            />
            <Route
              path="/user-profile"
              element={
                <PrivateRoute>
                  <UserProfile />
                </PrivateRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <PrivateRoute>
                  <Dashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/notifications"
              element={
                <PrivateRoute>
                  <NotificationsPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/settings/security"
              element={
                <PrivateRoute>
                  <SecurityPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/settings/privacy"
              element={
                <PrivateRoute>
                  <PrivacyPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/dashboard/webinar"
              element={
                <PrivateRoute>
                  <WebinarRegistration />
                </PrivateRoute>
              }
            />
            <Route
              path="/dashboard/certificate"
              element={
                <PrivateRoute>
                  <CertificateProgram />
                </PrivateRoute>
              }
            />
            <Route
              path="/dashboard/trends-source"
              element={
                <PrivateRoute>
                  <TrendsSource />
                </PrivateRoute>
              }
            />
            <Route
              path="/dashboard/trends"
              element={
                <PrivateRoute>
                  <TrendsListPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/dashboard/activity"
              element={
                <PrivateRoute>
                  <RecentActivityPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/activity"
              element={
                <PrivateRoute>
                  <RecentActivityPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/dashboard/negotiator-center"
              element={
                <PrivateRoute>
                  <NegotiatorCenter />
                </PrivateRoute>
              }
            />
            <Route
              path="/dashboard/references"
              element={
                <PrivateRoute>
                  <ReferenceFeedbackListPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/dashboard/references/:id"
              element={
                <PrivateRoute>
                  <ReferenceDetailPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/rate-me"
              element={
                <PrivateRoute>
                  <RateMeComingSoon />
                </PrivateRoute>
              }
            />
            <Route
              path="/get-started"
              element={
                <PrivateRoute>
                  <WelcomePage />
                </PrivateRoute>
              }
            />
            <Route
              path="/complete-profile-avatar"
              element={
                <PrivateRoute>
                  <CompleteProfileAvatar />
                </PrivateRoute>
              }
            />
            <Route
              path="/messages"
              element={
                <PrivateRoute>
                  <TailwindLayout>
                    <MessagesHub />
                  </TailwindLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/messages/questions"
              element={
                <PrivateRoute>
                  <TailwindLayout>
                    <QuestionsHub />
                  </TailwindLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/messages/contacts"
              element={
                <PrivateRoute>
                  <TailwindLayout>
                    <ContactsFlow />
                  </TailwindLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/messages/requests"
              element={
                <PrivateRoute>
                  <TailwindLayout>
                    <MessageRequestList />
                  </TailwindLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/calendar"
              element={
                <PrivateRoute>
                  <TailwindLayout>
                    <PublicCalendarView />
                  </TailwindLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/live-calendar"
              element={
                <PrivateRoute>
                  <TailwindLayout>
                    <PublicCalendarView />
                  </TailwindLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/prompts"
              element={
                <PrivateRoute>
                  <TailwindLayout>
                    <PromptsPage />
                  </TailwindLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/create-prompt"
              element={
                <PrivateRoute>
                  <CreatePrompt />
                </PrivateRoute>
              }
            />
            <Route
              path="/analytics"
              element={
                <PrivateRoute>
                  <TailwindLayout>
                    <Analytics />
                  </TailwindLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/candidate/:username"
              element={
                <PrivateRoute>
                  <CandidateProfile />
                </PrivateRoute>
              }
            />
            {/*  <Route
              path="/calendar"
              element={
                <PrivateRoute>
                  <LiveCalendarPage />
                </PrivateRoute>
              }
            /> */}
            <Route path="*" element={<PageNotFound />} />
            <Route
              path="/user-settings"
              element={
                <PrivateRoute>
                  <Settings />
                </PrivateRoute>
              }
            />
            <Route
              path="/account/connections"
              element={
                <PrivateRoute>
                  <AccountConnections />
                </PrivateRoute>
              }
            />
            <Route
              path="/chat"
              element={
                <PrivateRoute>
                  <ChatDemo />
                </PrivateRoute>
              }
            />
          </Route>
          <Route path="/:username/calendar" element={<PublicCalendarPage />} />
          <Route path="/u/:username" element={<PublicUserProfile />} />
          <Route path="/vvvv" element={<MultiStepFlow />} />
          <Route path="/aaaa" element={<ChatWindow />} />
          <Route path="/cccc" element={<ChatApp />} />
          <Route
            path="/demoquestionandchat"
            element={<ChatSidebar show={true} handleClose={() => {}} />}
          />
        </Routes>

        {/* </Router> */}
        <ToastContainer position="top-right" autoClose={3000} />
        <ClientUpdateBanner />
      </>
    </GoogleOAuthProvider>
  );
}

export default App;
